import { useTranslation } from 'react-i18next';
import { Copy, Send } from 'lucide-react';
import { Button, Modal, toast } from '@/ui';
import { copyText } from '@/lib/clipboard';
import { counterpartyName, type OfferRow } from '@/features/tasks/types';
import { BankPanel, InfoGrid, type InfoItem } from '@/features/tasks/components/QuoteCard';
import { payoutOf } from './payout';
import styles from './PayoutDialog.module.css';

/**
 * "ISLEMI TAMAMLA" — buronun ikinci adimi (2026-09-26).
 *
 * Eskiden "Odeme alindi" isi dogrudan Gecmis'e atiyordu; buronun musteriye GONDERIMI icin ne
 * bir ekran ne de bir durum vardi. Bu pencere gonderimin tek bakista yapilacagi yer:
 *   KIME        : musterinin talebe bagladigi hesap (`taskData.selectedAccount`). Sunucu bu
 *                 blob'u yalniz KAZANAN teklife aciyor; buradaki satir her zaman kazanan.
 *   NE KADAR    : payout.ts — buronun giden bacagi (alista miktar, satista toplam).
 *   HANGI NOTLA : `#talep` referansi; musteri gelen havaleyi bununla eslestirir.
 * IBAN BankPanel'de MASKELI durur (liste kurali, lib/iban-format.ts); kopyalama tam degeri verir.
 *
 * Onay acik bir dugmeyle verilir; perdeye tiklamak pencereyi KAPATMAZ (mobil PayoutSheet ile
 * ayni karar: IBAN kopyalayip banka uygulamasina gecen kullanici pencereyi kaybetmesin).
 */

export type PayoutDialogProps = {
  offer: OfferRow;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export function PayoutDialog({ offer, busy, onCancel, onConfirm }: PayoutDialogProps) {
  const { t } = useTranslation();
  const payout = payoutOf(offer);
  const account = offer.taskData?.selectedAccount;
  const reference = `#${offer.taskId}`;

  const info: InfoItem[] = [
    {
      // Kartla ayni etiket kurali (OfferCard): sirket adi varsa "Sirket", yoksa "Isim".
      label: offer.taskUserBusinessName ? t('labels.company') : t('labels.name'),
      text: counterpartyName(offer.taskUserBusinessName, offer.taskUserFullName) ?? '',
    },
    { label: t('labels.toSend'), total: payout.total, currency: payout.currency, emphasis: true },
  ];

  const copyReference = (): void => {
    void copyText(reference).then((copied) => {
      toast(
        copied
          ? { message: t('approval.referenceCopied'), tone: 'success' }
          : { message: t('approval.referenceCopyFailed'), tone: 'danger' },
      );
    });
  };

  const close = (): void => {
    // Istek surerken kapatmak sonucu (basari/hata mesaji) gozden kacirtirdi.
    if (!busy) onCancel();
  };

  return (
    <Modal
      open
      onClose={close}
      title={t('payoutSheet.title')}
      width="md"
      dismissOnScrim={false}
      footer={
        <>
          <Button tone="ghost" onClick={close} disabled={busy}>
            {t('actions.cancel')}
          </Button>
          <Button
            loading={busy}
            iconLeft={<Send size={16} aria-hidden="true" />}
            onClick={onConfirm}
          >
            {t('payoutSheet.confirm')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <p className={styles.description}>{t('payoutSheet.description')}</p>

        <InfoGrid items={info} />

        {account ? (
          <BankPanel account={account} />
        ) : (
          <p className={styles.accountMissing}>{t('approval.accountMissing')}</p>
        )}

        <div className={styles.referenceRow}>
          <div className={styles.referenceText}>
            <span className={styles.referenceLabel}>{t('approval.referenceLabel')}</span>
            <span className={styles.referenceValue}>{reference}</span>
          </div>
          <Button
            tone="neutral"
            size="sm"
            iconLeft={<Copy size={14} aria-hidden="true" />}
            onClick={copyReference}
          >
            {t('approval.copyReference')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
